import React from 'react';
import { cn } from '../../lib/utils';
import { RefreshCcw, History, Axis3d, Box, Orbit, Rotate3d, Play, Pause, X } from 'lucide-react';
import * as THREE from 'three';
import { dbgAutoplayStart } from '../../utils/debugTelemetry';
import { navSource, SRC } from '../../utils/navRecorder';

export interface FooterTerminalProps {
  historyOffset: number;
  uiHiddenLevel: number;
  showUiControls: boolean;
  interactionMode: 'orbit' | 'crosshair' | 'zone' | 'time';
  setInteractionMode: (mode: 'orbit' | 'crosshair' | 'zone' | 'time') => void;
  isReturningToLive: boolean;
  setIsReturningToLive: (val: boolean) => void;
    setHistoryOffset: React.Dispatch<React.SetStateAction<number>>;
  targetHistoryOffset: React.MutableRefObject<number>;
  returnSensitivity: number;
  setReturnSensitivity: React.Dispatch<React.SetStateAction<number>>;
  timeSensitivity: number;
  setTimeSensitivity: React.Dispatch<React.SetStateAction<number>>;
  setIsCrosshairActive: (val: boolean) => void;
  isCrosshairPinned: boolean;
  cameraTweenTarget: THREE.Vector3 | null;
  setCameraTweenTarget: (val: THREE.Vector3 | null) => void;
  setControlsTweenTarget: (val: THREE.Vector3 | null) => void;
  cameraViewMode: 'Z' | 'X' | 'Y';
  setCameraViewMode: (mode: 'Z' | 'X' | 'Y') => void;
  autoRotate: boolean;
  setAutoRotate: (val: boolean) => void;
  setIsCurrentlyIdle: (val: boolean) => void;
  idleTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  /** F3b C4: X durante a navegação histórica (sai do modo e deixa o gráfico onde está) */
  onExitHistory?: () => void;
}

export const FooterTerminal: React.FC<FooterTerminalProps> = ({

  historyOffset,
  uiHiddenLevel,
  showUiControls,
  interactionMode,
  setInteractionMode,
  isReturningToLive,
  setIsReturningToLive,
    setHistoryOffset,
  targetHistoryOffset,
  returnSensitivity,
  setReturnSensitivity,
  timeSensitivity,
  setTimeSensitivity,
  setIsCrosshairActive,
  isCrosshairPinned,
  cameraTweenTarget,
  setCameraTweenTarget,
  setControlsTweenTarget,
  cameraViewMode,
  setCameraViewMode,
  autoRotate,
  setAutoRotate,
  setIsCurrentlyIdle,
  idleTimerRef,
  onExitHistory,
}) => {
  // F3b C4: durante a navegação histórica o "Rotacionar" vira um X logo acima da barra
  const historyNavOn = interactionMode === 'time' && !isReturningToLive;
  const holdTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  return (
    <>
      {/* Time Navigation Mode Toggle */}
      <div className={cn(
        "absolute bottom-[calc(11.5rem+env(safe-area-inset-bottom,0px))] md:bottom-32 right-4 md:right-10 lg:right-10 z-20 flex flex-col items-center gap-3 transition-all duration-1000",
        uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible" : "opacity-100 visible"
      )}>
        <button
          onPointerDown={(e) => {
            e.stopPropagation();
            holdTimerRef.current = setTimeout(() => {
               // Hold triggered: Cancel return & freeze
               setIsReturningToLive(false);
               setInteractionMode('orbit');
               setHistoryOffset(prev => { navSource(SRC.CONGELAR); targetHistoryOffset.current = prev; navSource(0); return prev; });
               holdTimerRef.current = null;
            }, 500);
          }}
          onPointerUp={(e) => {
            e.stopPropagation();
            if (holdTimerRef.current) {
               clearTimeout(holdTimerRef.current);
               holdTimerRef.current = null;
               
               // Handle click
               if (!isReturningToLive) {
                 // Click 1: Return speed 1
                 dbgAutoplayStart(); // diagnóstico (?dbg=1): o alvo vai a 0 por projeto, não é recuo
                 setIsReturningToLive(true);
                 setReturnSensitivity(1);
                 navSource(SRC.AUTOPLAY); targetHistoryOffset.current = 0; navSource(0);
                 setInteractionMode('orbit');
                 setIsCrosshairActive(false);
               } else {
                 // Clicks 2, 3, 4
                 if (returnSensitivity === 1) {
                   setReturnSensitivity(2);
                 } else if (returnSensitivity === 2) {
                   setReturnSensitivity(3);
                 } else if (returnSensitivity === 3) {
                   // Click 4: Cancel return & freeze
                   setIsReturningToLive(false);
                   setInteractionMode('orbit');
                   setHistoryOffset(prev => { navSource(SRC.CONGELAR); targetHistoryOffset.current = prev; navSource(0); return prev; });
                 }
               }
            }
          }}
          onPointerLeave={(e) => {
            e.stopPropagation();
            if (holdTimerRef.current) {
               clearTimeout(holdTimerRef.current);
               holdTimerRef.current = null;
            }
          }}
          className={cn(
            "w-12 h-12 rounded-full border flex items-center justify-center transition-all backdrop-blur-md shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:scale-110 active:scale-95 relative cursor-pointer",
            (isReturningToLive)
              ? 'bg-[#00d9ff]/30 text-[#00d9ff] border-[#00d9ff]/60 shadow-[#00d9ff]/30'
              : 'bg-[#0a0e27]/80 text-[#666666] border-[#00d9ff]/30 hover:text-[#00d9ff] hover:bg-[#00d9ff]/10'
          )}
          title="Modo Histórico"
        >
          <History size={20} className={cn(isReturningToLive ? "animate-spin" : "")} style={{ animationDuration: isReturningToLive ? `${4 - returnSensitivity}s` : '0s' }} />
        </button>
      </div>

      {/* Fixed Camera View Controls */}
      <div className={cn(
        "absolute bottom-[calc(11.5rem+env(safe-area-inset-bottom,0px))] md:bottom-32 left-4 md:left-10 lg:left-10 z-20 flex flex-col items-center gap-3 transition-all duration-1000",
        uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible" : "opacity-100 visible"
      )}>
        <button
          disabled={cameraTweenTarget !== null}
          onClick={(e) => { 
            e.stopPropagation(); 
            if (cameraTweenTarget !== null) return;
            const distZ = window.innerWidth < 768 ? 8000 : 6000;
            const distX = window.innerWidth < 768 ? 6500 : 4700;
            const distY = window.innerWidth < 768 ? 4500 : 3100; 
            if (cameraViewMode === 'Z') {
              setCameraTweenTarget(new THREE.Vector3(distX, 150, 0)); 
              setControlsTweenTarget(new THREE.Vector3(0, 0, 0));
              setCameraViewMode('X');
            } else if (cameraViewMode === 'X') {
              setCameraTweenTarget(new THREE.Vector3(0, distY, 150)); 
              setControlsTweenTarget(new THREE.Vector3(0, 0, 0));
              setCameraViewMode('Y');
            } else {
              setCameraTweenTarget(new THREE.Vector3(0, 150, distZ)); 
              setControlsTweenTarget(new THREE.Vector3(0, 0, 0));
              setCameraViewMode('Z');
            }
          }}
          className={`w-12 h-12 rounded-full border flex items-center justify-center font-black text-[14px] transition-all backdrop-blur-md shadow-[0_0_20px_rgba(0,0,0,0.5)] ${cameraTweenTarget === null ? "hover:scale-110 active:scale-95 cursor-pointer" : "opacity-50 cursor-not-allowed"} ${
            cameraViewMode === 'Z' 
              ? 'bg-[#3b82f6]/30 text-[#3b82f6] border-[#3b82f6]/60 shadow-[#3b82f6]/30'
              : cameraViewMode === 'X'
                ? 'bg-[#ef4444]/30 text-[#ef4444] border-[#ef4444]/60 shadow-[#ef4444]/30'
                : 'bg-[#10b981]/30 text-[#10b981] border-[#10b981]/60 shadow-[#10b981]/30'
          }`}
          title="Mudar Vista"
        >
          {cameraViewMode === "Z" ? <Axis3d size={24} /> : cameraViewMode === "X" ? <Box size={24} /> : <Orbit size={24} />}
        </button>
      </div>

      {/* F3b C4: X durante a navegação histórica — sai do modo e deixa o gráfico onde está (não volta ao presente).
          Logo acima da barra, no lugar do "Rotacionar"; círculo de 36 px dentro de uma área de toque de 48 × 48 px. */}
      {historyNavOn && (
        <div
          className={cn(
            "absolute bottom-[calc(4.25rem+env(safe-area-inset-bottom,0px))] md:bottom-[4.75rem] left-1/2 -translate-x-1/2 z-20 transition-all duration-300",
            uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible" : "opacity-100 visible"
          )}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          <button
            data-testid="history-exit"
            aria-label="Sair da navegação histórica"
            title="Sair da navegação histórica"
            onClick={(e) => { e.stopPropagation(); onExitHistory?.(); }}
            className="w-12 h-12 flex items-center justify-center cursor-pointer group"
          >
            <span className="w-9 h-9 rounded-full flex items-center justify-center bg-[#0a0e27]/80 backdrop-blur-md border border-[#00d9ff]/40 text-[#00d9ff]/80 shadow-[0_0_12px_rgba(0,217,255,0.25)] transition-all group-hover:text-[#00d9ff] group-hover:border-[#00d9ff] group-active:scale-95">
              <X size={16} strokeWidth={2.5} />
            </span>
          </button>
        </div>
      )}

      {/* Interaction Mode Toggle (Bottom Center) */}
      <div 
        className={cn(
          "absolute bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center bg-[#0a0e27]/80 backdrop-blur-md border border-[#00d9ff]/30 rounded-full overflow-x-auto no-scrollbar p-1 gap-1 max-w-[95vw] transition-all duration-300",
          uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible translate-y-8" : "opacity-100 visible translate-y-0"
        )}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center shrink-0">
          {!historyNavOn && (<>
          <button
            onClick={() => {
              setInteractionMode('orbit');
              if (!isCrosshairPinned) {
                setIsCrosshairActive(false);
              }
            }}
            className={cn(
              "flex items-center gap-2 pl-4 pr-3 py-2 text-[10px] uppercase tracking-[1px] transition-all duration-200 rounded-l-full cursor-pointer",
              interactionMode === 'orbit' 
                ? "bg-[#00d9ff]/20 text-[#00d9ff] font-bold" 
                : "text-[#666666] hover:text-[#00d9ff] hover:bg-[#00d9ff]/5"
            )}
          >
            <Rotate3d size={14} /> Rotacionar
          </button>
          
          <div className="w-[1px] h-6 shrink-0 bg-[#00d9ff]/30 mx-1" />
          </>)}
          
          {!autoRotate ? (
            <button
              onClick={() => {
                setAutoRotate(true);
                setInteractionMode('orbit');
                setIsCrosshairActive(false);
                setTimeout(() => {
                  setIsCurrentlyIdle(true);
                  if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
                }, 50);
              }}
              className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[1px] transition-all duration-200 text-[#00ffa3] hover:bg-[#00ffa3]/20 hover:text-[#00ffa3] cursor-pointer"
              title="Ativar Rotação Automática"
            >
              <Play size={14} /> Auto
            </button>
          ) : (
            <button
              onClick={() => {
                setAutoRotate(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[1px] transition-all duration-200 text-[#00d9ff]/80 hover:bg-[#00d9ff]/20 hover:text-[#00d9ff] cursor-pointer"
              title="Pausar Rotação Automática"
            >
              <Pause size={14} /> Pausar
            </button>
          )}

          <div className="w-[1px] h-6 shrink-0 bg-[#00d9ff]/30 mx-1" />
        </div>
      </div>
    </>
  );
};

export default FooterTerminal;
